import { Stack } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import {
  COLOR_BY_ASSIGNMENT_STATUS,
  newsRecipientsFiltersSchema,
  useAssignRecipients,
  useNewsRecipients,
} from 'store/news';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import { useSearchParams } from 'utils/navigation';
import {
  CompaniesFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { assignmentStatuses } from 'store/assignments';
import NumberRangeFilter from 'components/filters/NumberRangeFilter';
import { useMe } from 'state/Administrators';
import qs from 'qs';

interface Props {
  onSaveSuccess: () => void;
}

export const NewRecipientsList: FC<Props> = ({ onSaveSuccess }) => {
  const { canEditNews, hasEditPermission } = useMe();
  const { t } = useTranslation();
  const { id: newId } = useRouteParams(ROUTES.news.details);
  const predefinedToasts = usePredefinedToasts();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();

  const { searchParams: filters, navigate } = useSearchParams(
    newsRecipientsFiltersSchema,
  );

  const { data: recipients, isLoading } = useNewsRecipients(newId, filters);

  const { mutate: assignRecipients, isLoading: isAssigning } =
    useAssignRecipients();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <ListTotal total={recipients?.total} />

          <Filters
            value={filters}
            initialValues={newsRecipientsFiltersSchema.parse({})}
            onChange={(v) => navigate({ params: { ...v, page: 1 } })}
          >
            {({ value, initialValues, setFilter }) => (
              <>
                <FilterButton
                  label={t('employees.company')}
                  value={value.companies}
                  initialValue={initialValues.companies}
                  onChange={(v) => setFilter('companies', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <CompaniesFilter
                      value={value}
                      onApplyFilter={applyFilter}
                      onClearFilter={clearFilter}
                      onCancel={close}
                      multiple
                    />
                  )}
                </FilterButton>

                <FilterButton
                  label={t('exportFileName.employees')}
                  value={value.employeeCount}
                  initialValue={initialValues.employeeCount}
                  onChange={(v) => setFilter('employeeCount', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <NumberRangeFilter
                      value={value}
                      onApplyFilter={applyFilter}
                      onClearFilter={clearFilter}
                      onCancel={close}
                    />
                  )}
                </FilterButton>

                <FilterButton
                  label={t('alternatives.assignmentStatus')}
                  value={value.assignmentStatus}
                  initialValue={initialValues.assignmentStatus}
                  onChange={(v) => setFilter('assignmentStatus', v)}
                >
                  {({ value, applyFilter, clearFilter, close }) => (
                    <SelectFilter
                      value={{ value }}
                      onApplyFilter={({ value }) => applyFilter(value)}
                      onClearFilter={clearFilter}
                      onCancel={close}
                      options={assignmentStatuses.map((status) => ({
                        key: status,
                        label: t(`status.${status}`),
                      }))}
                      searchable={false}
                      multiple={true}
                    />
                  )}
                </FilterButton>

                <OpenSearchFilter
                  value={value.search}
                  onApplyFilter={(v) => setFilter('search', v)}
                  placeholder={t('common.search')}
                />
              </>
            )}
          </Filters>
        </Stack>
      </TableToolbar>

      <Table
        data={recipients?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('groups.group_name'),
            cell: ({ row: { original: recipient } }) => (
              <Link
                label={recipient.name}
                to={{
                  pathname: ROUTES.employeeGroups.details.replace(
                    ':id',
                    recipient.id,
                  ),
                  search: qs.stringify({ tab: 'innerGroupAssignEmployees' }),
                }}
              />
            ),
            meta: { sort: true },
          },
          {
            id: 'employeeCount',
            header: t('exportFileName.employees'),
            cell: ({ row: { original: recipient } }) => recipient.employeeCount,
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'assignmentStatus',
            header: t('alternatives.assignmentStatus'),
            meta: { align: 'center' },
            cell: ({ row: { original: recipient } }) => {
              return (
                <Typography
                  color={COLOR_BY_ASSIGNMENT_STATUS[recipient.assignmentStatus]}
                >
                  {t(`status.${recipient.assignmentStatus}`)}
                </Typography>
              );
            },
          },
          {
            id: 'actions',
            cell: ({ row: { original: recipient } }) => {
              return (
                <ActionsMenu
                  type="compact"
                  items={[
                    {
                      label: t(
                        `status.${
                          recipient.assignmentStatus === 'on' ? 'off' : 'on'
                        }`,
                      ),
                      disabled: !canEditNews || !hasEditPermission(['news']),
                      onClick: async () => {
                        assignRecipients(
                          {
                            newId,
                            employeeGroupIds: [recipient.id],
                            status:
                              recipient.assignmentStatus === 'on'
                                ? 'off'
                                : 'on',
                          },
                          {
                            onSuccess: () => {
                              predefinedToasts.success.updated();
                              onSaveSuccess();
                            },
                          },
                        );
                      },
                    },
                  ]}
                />
              );
            },
          },
        ]}
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          actions: assignmentStatuses.map((status) => ({
            label: t(`status.${status}`),
            disabled: !canEditNews || !hasEditPermission(['news']),
            onClick: () => {
              assignRecipients(
                {
                  newId,
                  employeeGroupIds: Object.keys(selectedRowIds),
                  status,
                },
                {
                  onSuccess: () => {
                    predefinedToasts.success.updated();
                    setSelectedRowIds({});
                    onSaveSuccess();
                  },
                },
              );
            },
          })),
          isLoading: isAssigning,
        }}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          recipients && {
            count: recipients.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
